<template>
  <div v-if="shouldShowNotification" class="event-notification">
    <VaAlert
      class="event-alert"
      :color="eventTypeToColor[latestEvent?.type || 'info'] || 'info'"
      :title="formatEventTitle(latestEvent?.type || 'info')"
      closeable
      @close="dismissNotification"
    >
      {{ formatEventMessage(latestEvent) }}
      <template #append>
        <VaButton size="small" :to="{ name: 'events' }" class="ma-2"> View All Events </VaButton>
      </template>
    </VaAlert>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useEventsStore } from '../../stores/events-store'

const eventsStore = useEventsStore()
const showNotification = ref(false)
const latestEventIndex = ref(0)

// Map event types to colors
const eventTypeToColor: Record<string, string> = {
  connected: 'success',
  notification: 'info',
  alert: 'warning',
  error: 'danger',
  update: 'primary',
  info: 'info',
  warning: 'warning',
  success: 'success',
  message: 'primary',
}

// Format the event title for display
const formatEventTitle = (type: string) => {
  return type.charAt(0).toUpperCase() + type.slice(1) + ' Event'
}

// Format the event message for display
const formatEventMessage = (event: any) => {
  if (typeof event.data === 'object') {
    // Try to extract a message from the data
    if (event.data.message) {
      return event.data.message
    } else if (event.data.content) {
      return event.data.content
    } else {
      return `Received ${event.type} event`
    }
  }
  return `New ${event.type} event received`
}

// Computed property to get the latest event
const latestEvent = computed(() => {
  if (eventsStore.events.length > latestEventIndex.value) {
    return eventsStore.events[latestEventIndex.value]
  }
  return null
})

// Computed property to determine if notification should be shown
const shouldShowNotification = computed(() => {
  console.log(
    'Checking if notification should be shown:',
    showNotification.value,
    latestEvent.value?.data?.Message?.SenderType,
  )
  return (
    showNotification.value &&
    latestEvent.value &&
    latestEvent.value.type !== 'connected' &&
    latestEvent.value.type !== 'heartbeat' &&
    latestEvent.value.type !== 'new_message' &&
    latestEvent.value.data?.Message?.SenderType !== 'agent'
  )
})

// Watch for new events and show notification
watch(
  () => eventsStore.events.length,
  (newLength, oldLength) => {
    if (newLength > oldLength && newLength > 0) {
      // New event added, show notification
      latestEventIndex.value = 0 // Reset to the newest event
      showNotification.value = true

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        showNotification.value = false
      }, 5000)
    }
  },
)

// Function to dismiss the notification
const dismissNotification = () => {
  showNotification.value = false
}
</script>

<style lang="scss" scoped>
.event-notification {
  position: fixed;
  top: 70px;
  right: 20px;
  z-index: 1000;
  width: 350px;

  .event-alert {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}
</style>
